import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import config from "../../common/Config";
import { toast } from "sonner";
import {
  Box,
  Typography,
  TextField,
  Paper,
  InputAdornment,
  TablePagination,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Skeleton,
  Link as MuiLink,
} from '@mui/material';
import { Iconify } from '../../Minimal/src/components/iconify/iconify';
import '../../common/commonStyles.css';

const ContraList = () => {
  const [customers, setCustomers] = useState([]);
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("draw", page + 1);
      formData.append("start", page * rowsPerPage);
      formData.append("length", rowsPerPage);
      formData.append("search[value]", searchValue || "");
      formData.append("search[regex]", "false");

      const response = await fetch(`${config.API_URL}/contra_price_list`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const json = await response.json();

      if (json?.data && Array.isArray(json.data)) {
        setCustomers(json.data);
        setTotalRecords(json.recordsTotal || json.recordsFiltered || json.data.length);
      } else {
        setCustomers([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch contra price list");
      setCustomers([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchValue, accessToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    setSearchValue(value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <Box>
      <Box className="page-container">
        <Paper className="paper-style" >
          <Box sx={{ px: 2, py: 1.2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Typography className="page-title">
                  Contra Price List
                </Typography>
                <TextField
                  className="input-border"
                  size="small"
                  placeholder="Search customers..."
                  value={searchInputValue}
                  onChange={handleSearchChange}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline"/>
                        </InputAdornment>
                      )
                    }
                  }}
                  sx={{
                    width: { xs: '100%', sm: 400 },
                  }}
                />
              </Box>
            </Box>
            <TableContainer
            >
              <Table
                size="medium"
                className="table-cell-border"
                sx={{ tableLayout: "fixed"}}
              >
                <TableHead>
                  <TableRow className="table-cell-header">
                    {["#", "Customer Name", "Email", "Phone", "Account No", "Postal Code"].map((header) => {
                      let align = "left";
                      if (header === "#" || header === "Account No" || header === "Postal Code") {
                        align = "center";
                      }
                      return (
                        <TableCell
                          key={header}
                          align={align}
                          sx={{
                            width: header === "#" ? '70px' : header === "Email" ? '300px' : header === "Phone" ? '150px' : header === "Account No" ? '150px' : 'auto',
                          }}
                        >
                          {header}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell colSpan={6}>
                          <Skeleton height={40} />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : customers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography className="commonColor">
                          No records found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    customers.map((row, index) => (
                      <TableRow
                        key={row.id || index}
                        hover
                        className="table-cell-style"
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(145, 158, 171, 0.04)"
                          },
                      
                        }}
                      >
                        <TableCell align="center" width="70px">
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell>
                          <MuiLink
                            href={`${config.BASE_URL}/customers/contra_price_edit/${row.id}`}
                            sx={{
                              color: "inherit",
                              textDecoration: "underline !important",
                              textDecorationColor: "currentColor",
                              textUnderlineOffset: "2px",
                              "&:hover": {
                                textDecoration: "underline !important",
                                textDecorationColor: "currentColor"
                              }
                            }}
                          >
                            {row.company}
                          </MuiLink>
                        </TableCell>
                        <TableCell align="left" width="300px">
                          {row.email || ''}
                        </TableCell>
                        <TableCell align="left" width="150px">
                          {row.phone || ''}
                        </TableCell>
                        <TableCell align="center" width="150px">
                          {row.accound_no || ''}
                        </TableCell>
                        <TableCell align="center">
                          {row.postal_code || ''}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {customers.length > 0 && (
              <Box display="flex" justifyContent="flex-end" alignItems="center">
                <TablePagination
                  component="div"
                  count={totalRecords}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  className="pagination-styles"
                />
              </Box>
            )}
          </Paper>
      </Box>
    </Box>
  );
};

export default ContraList;
